/**
 * CR140: Hook for managing batch run schedules
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { 
  BatchRunSchedule, 
  BatchRunScheduleRun, 
  ScheduleWithMerchants,
  ScheduleFormData,
  MAX_EXPECTED_RUNS 
} from '@/lib/scheduler/types';

export function useSchedules() {
  const [schedules, setSchedules] = useState<ScheduleWithMerchants[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch schedules
      const { data: schedulesData, error: schedulesError } = await supabase
        .from('batch_run_schedules')
        .select('*')
        .order('created_at', { ascending: false });

      if (schedulesError) throw schedulesError;

      // Fetch merchants for all schedules
      const { data: merchantsData, error: merchantsError } = await supabase
        .from('batch_run_schedule_merchants')
        .select('schedule_id, merchant_id');

      if (merchantsError) throw merchantsError;

      // Fetch latest run for each schedule
      const scheduleIds = schedulesData?.map(s => s.id) || [];
      const { data: runsData, error: runsError } = await supabase
        .from('batch_run_schedule_runs')
        .select('*')
        .in('schedule_id', scheduleIds)
        .order('scheduled_for', { ascending: false });

      if (runsError) throw runsError;

      // Group merchants by schedule
      const merchantsBySchedule: Record<string, string[]> = {};
      merchantsData?.forEach(m => {
        if (!merchantsBySchedule[m.schedule_id]) {
          merchantsBySchedule[m.schedule_id] = [];
        }
        merchantsBySchedule[m.schedule_id].push(m.merchant_id);
      });

      // Get latest run by schedule
      const latestRunBySchedule: Record<string, BatchRunScheduleRun> = {};
      runsData?.forEach(run => {
        if (!latestRunBySchedule[run.schedule_id]) {
          latestRunBySchedule[run.schedule_id] = run as BatchRunScheduleRun;
        }
      });

      // Combine data
      const combined: ScheduleWithMerchants[] = (schedulesData || []).map(schedule => ({
        ...schedule,
        sources_enabled: schedule.sources_enabled || [],
        source_configs: schedule.source_configs || {},
        merchants: merchantsBySchedule[schedule.id] || [],
        merchant_count: (merchantsBySchedule[schedule.id] || []).length,
        last_run: latestRunBySchedule[schedule.id] || null,
      })) as ScheduleWithMerchants[];

      setSchedules(combined);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch schedules';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const createSchedule = async (formData: ScheduleFormData): Promise<string | null> => {
    try {
      const expectedRuns = formData.merchant_ids.length * formData.sources_enabled.length;
      if (expectedRuns > 50) {
        throw new Error(`Expected runs (${expectedRuns}) exceeds limit of 50`);
      }

      // Create schedule
      const { data: schedule, error: scheduleError } = await supabase
        .from('batch_run_schedules')
        .insert([{
          name: formData.name,
          is_enabled: formData.is_enabled,
          timezone: formData.timezone,
          sources_enabled: formData.sources_enabled,
          source_configs: formData.source_configs as unknown as Record<string, never>,
          max_concurrent_runs: formData.max_concurrent_runs,
          run_at: formData.run_at?.toISOString() || null,
          schedule_type: 'ONE_TIME' as const,
          // CR156: Use provided checkpoint or default to STOP_AFTER_NORMALIZE_DEDUP
          approval_checkpoint: formData.approval_checkpoint || 'STOP_AFTER_NORMALIZE_DEDUP' as const,
          // CR168: Flyer bucket source ID
          flyer_bucket_source_id: formData.flyer_bucket_source_id || null,
        }])
        .select()
        .single();

      if (scheduleError) throw scheduleError;

      // Create merchant associations
      if (formData.merchant_ids.length > 0) {
        const merchantRows = formData.merchant_ids.map(merchant_id => ({
          schedule_id: schedule.id,
          merchant_id,
        }));

        const { error: merchantError } = await supabase
          .from('batch_run_schedule_merchants')
          .insert(merchantRows);

        if (merchantError) throw merchantError;
      }

      toast({
        title: 'Schedule Created',
        description: `Schedule "${formData.name}" created successfully.`,
      });

      await fetchSchedules();
      return schedule.id;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create schedule';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return null;
    }
  };

  const updateSchedule = async (id: string, formData: Partial<ScheduleFormData>): Promise<boolean> => {
    try {
      // Update schedule
      const updateData: Record<string, unknown> = {};
      if (formData.name !== undefined) updateData.name = formData.name;
      if (formData.is_enabled !== undefined) updateData.is_enabled = formData.is_enabled;
      if (formData.timezone !== undefined) updateData.timezone = formData.timezone;
      if (formData.sources_enabled !== undefined) updateData.sources_enabled = formData.sources_enabled;
      if (formData.source_configs !== undefined) updateData.source_configs = formData.source_configs;
      if (formData.max_concurrent_runs !== undefined) updateData.max_concurrent_runs = formData.max_concurrent_runs;
      if (formData.run_at !== undefined) updateData.run_at = formData.run_at?.toISOString() || null;
      // CR156: Include approval_checkpoint in updates
      if (formData.approval_checkpoint !== undefined) updateData.approval_checkpoint = formData.approval_checkpoint;
      // CR168: Include flyer_bucket_source_id in updates
      if (formData.flyer_bucket_source_id !== undefined) updateData.flyer_bucket_source_id = formData.flyer_bucket_source_id;

      const { error: scheduleError } = await supabase
        .from('batch_run_schedules')
        .update(updateData)
        .eq('id', id);

      if (scheduleError) throw scheduleError;

      // Update merchants if provided
      if (formData.merchant_ids !== undefined) {
        // Delete existing
        await supabase
          .from('batch_run_schedule_merchants')
          .delete()
          .eq('schedule_id', id);

        // Insert new
        if (formData.merchant_ids.length > 0) {
          const merchantRows = formData.merchant_ids.map(merchant_id => ({
            schedule_id: id,
            merchant_id,
          }));

          const { error: merchantError } = await supabase
            .from('batch_run_schedule_merchants')
            .insert(merchantRows);

          if (merchantError) throw merchantError;
        }
      }

      toast({
        title: 'Schedule Updated',
        description: 'Schedule updated successfully.',
      });

      await fetchSchedules();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update schedule';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return false;
    }
  };

  const toggleEnabled = async (id: string, enabled: boolean): Promise<boolean> => {
    return updateSchedule(id, { is_enabled: enabled });
  };

  const deleteSchedule = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('batch_run_schedules')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Schedule Deleted',
        description: 'Schedule deleted successfully.',
      });

      await fetchSchedules();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete schedule';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return false;
    }
  };

  const runNow = async (id: string): Promise<boolean> => {
    try {
      // Step 1: Dispatch - creates batch runs with pipeline steps
      const { data, error } = await supabase.functions.invoke('scheduler-dispatch', {
        body: { schedule_id: id, run_now: true },
      });

      if (error) throw error;

      // Check for dispatch failure with diagnostics
      if (data?.error === "DISPATCH_FAILED") {
        const diagnostics = data?.diagnostics;
        let details = data?.message || "Dispatch failed";
        
        // Build detailed diagnostic message
        if (diagnostics) {
          const parts: string[] = [];
          parts.push(`Merchants: ${diagnostics.merchant_count}`);
          parts.push(`Sources: ${diagnostics.sources_enabled?.join(', ') || 'none'}`);
          if (diagnostics.missing_config_fields?.length > 0) {
            parts.push(`Missing: ${diagnostics.missing_config_fields.join(', ')}`);
          }
          parts.push(`Planned runs: ${diagnostics.computed_run_plan_count}`);
          details = `${data?.message}\n\n${parts.join('\n')}`;
        }

        toast({
          title: 'Dispatch Failed',
          description: details,
          variant: 'destructive',
        });
        return false;
      }

      const expectedCount = data?.expected_run_count || 0;
      const createdCount = data?.created_run_count || 0;
      const scheduleRunId = data?.schedule_run_id;

      // Step 2: If runs were created, immediately trigger the runner to start them
      if (createdCount > 0 && !data?.already_dispatched) {
        console.log(`[useSchedules] Triggering scheduler-runner to start ${createdCount} batch runs`);
        
        const { error: runnerError } = await supabase.functions.invoke('scheduler-runner', {
          body: {},
        });

        // Check for count mismatch
        const hasMismatch = createdCount < expectedCount;

        if (runnerError) {
          console.error('[useSchedules] Runner invoke error:', runnerError);
          // Don't fail - dispatch succeeded, runs will start on next runner cycle
          toast({
            title: hasMismatch ? 'Schedule Dispatched (with issues)' : 'Schedule Dispatched',
            description: `Created ${createdCount}/${expectedCount} batch runs.${hasMismatch ? ' Some runs may have failed to create.' : ''} Runner will start them shortly.`,
            variant: hasMismatch ? 'destructive' : 'default',
          });
        } else {
          toast({
            title: hasMismatch ? 'Schedule Triggered (with issues)' : 'Schedule Triggered',
            description: `Created ${createdCount}/${expectedCount} batch runs.${hasMismatch ? ' View Run History for details.' : ''}`,
            variant: hasMismatch ? 'destructive' : 'default',
          });
        }
      } else if (data?.already_dispatched) {
        toast({
          title: 'Already Dispatched',
          description: 'A run already exists for this schedule at this time.',
        });
      } else if (data?.success && createdCount === 0) {
        // This shouldn't happen if dispatch succeeded, but handle edge case
        toast({
          title: 'Schedule Triggered',
          description: 'Dispatch completed but no batch runs were created.',
          variant: 'destructive',
        });
      }

      await fetchSchedules();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to trigger schedule';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return false;
    }
  };

  /**
   * Stop (cancel) an active schedule run
   * Cancels the schedule_run and all associated batch runs that are still running
   */
  const stopRun = async (scheduleId: string): Promise<boolean> => {
    try {
      // Find the active schedule_run (PENDING or DISPATCHED)
      const { data: activeRun, error: findError } = await supabase
        .from('batch_run_schedule_runs')
        .select('id, status')
        .eq('schedule_id', scheduleId)
        .in('status', ['PENDING', 'DISPATCHED'])
        .order('scheduled_for', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (findError) throw findError;

      if (!activeRun) {
        toast({
          title: 'No Active Run',
          description: 'No active run to cancel.',
        });
        return false;
      }

      // 1. Cancel the schedule_run (use type assertion as DB enum was just updated)
      const { error: runError } = await supabase
        .from('batch_run_schedule_runs')
        .update({ status: 'CANCELLED' as any, updated_at: new Date().toISOString() })
        .eq('id', activeRun.id);

      if (runError) throw runError;

      // 2. Find all batch runs associated with this schedule_run
      const { data: batchRuns, error: batchFindError } = await supabase
        .from('cr075_batch_runs')
        .select('id, status')
        .eq('schedule_run_id', activeRun.id)
        .in('status', ['CREATED', 'RUNNING']);

      if (batchFindError) throw batchFindError;

      let cancelledBatchRuns = 0;
      let cancelledSteps = 0;

      // 3. Cancel each batch run and its non-terminal steps
      for (const batchRun of batchRuns || []) {
        // Cancel non-terminal steps
        const { data: stepsUpdated, error: stepsError } = await supabase
          .from('cr075_batch_run_steps')
          .update({ status: 'CANCELLED', finished_at: new Date().toISOString() })
          .eq('batch_run_id', batchRun.id)
          .in('status', ['NOT_STARTED', 'ENQUEUED', 'RUNNING'])
          .select('id');

        if (stepsError) {
          console.error('[stopRun] Failed to cancel steps:', stepsError);
        } else {
          cancelledSteps += stepsUpdated?.length || 0;
        }

        // Cancel the batch run itself
        const { error: cancelError } = await supabase
          .from('cr075_batch_runs')
          .update({ status: 'CANCELLED', updated_at: new Date().toISOString() })
          .eq('id', batchRun.id);

        if (cancelError) {
          console.error('[stopRun] Failed to cancel batch run:', cancelError);
        } else {
          cancelledBatchRuns++;
        }
      }

      toast({
        title: 'Run Cancelled',
        description: `Cancelled ${cancelledBatchRuns} batch run(s) and ${cancelledSteps} step(s).`,
      });

      await fetchSchedules();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to stop run';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    schedules,
    loading,
    error,
    fetchSchedules,
    createSchedule,
    updateSchedule,
    toggleEnabled,
    deleteSchedule,
    runNow,
    stopRun,
  };
}
